<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  words: {
    type: Array,
    required: true,
  },
  interval: {
    type: Number,
    default: 3000,
  },
  extrawords: {
    type: String,
    default: '',
  },
  extrawords2: {
    type: String,
    default: '',
  },
  colors: {
    type: Array,
    default: () => [
      'text-green-200', 'text-green-400', 'text-green-600',
      'text-emerald-200', 'text-emerald-400', 'text-emerald-600',
      'text-lime-200', 'text-lime-400', 'text-lime-600'
    ]
  },
  textAlignment: {
    type: [String, Object],
    default: 'text-right',
  },
});

const currentIndex = ref(0);
const isAnimating = ref(false);
const timer = ref(null);
const colorIndex = ref(0);

// Get current color from the colors array
const currentColor = computed(() => {
  return props.colors[colorIndex.value % props.colors.length];
});

// Simple rotating animation with color cycling
const rotateWords = () => {
  if (isAnimating.value) return;
  
  isAnimating.value = true;
  
  setTimeout(() => {
    currentIndex.value = (currentIndex.value + 1) % props.words.length;
    // Change color when words change
    colorIndex.value = (colorIndex.value + 1) % props.colors.length;
    isAnimating.value = false;
  }, 500); // Half the transition time
};

onMounted(() => {
  timer.value = setInterval(rotateWords, props.interval);
  window.addEventListener('resize', updateWindowWidth);
});

onBeforeUnmount(() => {
  if (timer.value) clearInterval(timer.value);
  window.removeEventListener('resize', updateWindowWidth);
});

// Update useHorizontalAnimation when window resizes
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0);

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};
</script>

<template>
  <!-- Single flex container with responsive layout -->
  <div class="inline-flex items-center w-full justify-center flex-col md:flex-row md:justify-between">
    <!-- Three-column layout on md+ screens, stacked on mobile -->
    <div class="flex flex-col md:flex-row w-full items-center">
      <!-- Left column: animated words -->
      <div class="relative overflow-visible w-full md:w-1/3 h-[1em] md:h-[1.2em] mb-2 md:mb-0 md:text-right">
        <div v-for="(word, index) in words" 
             :key="index" 
             :class="[
               'word', 
               index === currentIndex ? currentColor : '', 
               { 
                 'active': index === currentIndex, 
                 'previous': (index === ((currentIndex - 1 + words.length) % words.length)),
                 'responsive-animation': windowWidth < 768 
               }
             ]">
          {{ word }}
        </div>
      </div>
      
      <!-- Middle column: extrawords (centered) -->
      <span class="text-center md:w-auto whitespace-nowrap px-2 md:px-4 sm:w-1/2">
        {{ extrawords }}
      </span>
      
      <!-- Right column: extrawords2 (left-aligned) -->
      <span class="whitespace-nowrap md:w-1/3 md:text-left sm:w-1/2">
        {{ extrawords2 }}
      </span>
    </div>
  </div>
</template>

<style scoped>
/* Standard vertical animation (default) */
.word {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.5s ease;
}

.word.active {
  opacity: 1;
  transform: translateY(0);
}

.word.previous {
  opacity: 0;
  transform: translateY(-50px);
}

/* Horizontal animation (for responsive layout) */
.word.responsive-animation {
  transform: translateX(50px);
}

.word.responsive-animation.active {
  opacity: 1;
  transform: translateX(0);
}

.word.responsive-animation.previous {
  opacity: 0;
  transform: translateX(-50px);
}

/* Ensure proper alignment in the flex container */
@media (min-width: 768px) {
  .word {
    text-align: right;
    right: 0;
    left: auto;
    width: max-content;
  }
}
</style>
